import React, { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { ApolloError, useApolloClient } from "@apollo/client";
import { gql } from "__generated__/gql";
import { ExportCustomersQuery } from "__generated__/gql/graphql";
import { useCurrentBrandData } from "utils/currentUserHooks";
import ToastError from "utils/ToastError";
import asyncVoidHandler from "utils/asyncVoidHandler";
import type { ValidatedData } from "utils/xlsxImportExport/XlsxImportWorkBook";
import {
  ColumnFormatsByColumnBySheet,
  getWorkBookExport,
} from "utils/xlsxImportExport/export";
import ExportButton from "components/Shared/ExportButton";
import {
  CustomerRow,
  WarehouseRow,
  WorkSheetDefinitions,
  WORKSHEET_DEFINITIONS,
} from "utils/xlsxDefinitions/customers";
import { assertPresence } from "utils/assertions";

const query = gql(/* GraphQL */ `
  query ExportCustomers($brandId: UUID!) {
    brandOrderingPlatformsList(
      condition: { brandId: $brandId }
      orderBy: ORDERING_PLATFORM__NAME_ASC
    ) {
      orderingPlatform {
        name
        forBrandId
        warehouses: distributionCentersList(
          filter: { name: { isNull: false } }
          orderBy: NAME_ASC
        ) {
          name
          code
          notes
          phoneNumber {
            phoneNational: phoneFormatted(format: NATIONAL)
          }
          address {
            line1
            line2
            city
            state
            zip
          }
        }
      }
    }
  }
`);

const columnFormats: ColumnFormatsByColumnBySheet<WorkSheetDefinitions> = {
  customers: {
    name: {
      columnInfo: { wch: 25 },
    },
  },
  warehouses: {
    customerName: { columnInfo: { wch: 25 } },
    name: { columnInfo: { wch: 25 } },
  },
};

function getSheetData(
  customersData: NonNullable<
    ExportCustomersQuery["brandOrderingPlatformsList"]
  >,
): ValidatedData<WorkSheetDefinitions> {
  const warehouses: WarehouseRow[] = [];
  const customers = customersData.map<CustomerRow>((bop) => {
    assertPresence(bop.orderingPlatform);

    const {
      forBrandId,
      warehouses: rawWarehouses,
      ...restCustomer
    } = bop.orderingPlatform;

    const { name: customerName } = restCustomer;

    rawWarehouses?.forEach((warehouse) => {
      const { name, phoneNumber, address, ...restWarehouse } = warehouse;
      if (name) {
        warehouses.push({
          customerName,
          name,
          ...restWarehouse,
          phone: phoneNumber?.phoneNational,
          addressLine1: address?.line1,
          addressLine2: address?.line2,
          addressCity: address?.city,
          addressState: address?.state,
          addressZip: address?.zip,
        });
      }
    });

    return { ...restCustomer, isDefault: !forBrandId };
  });
  return { customers, warehouses };
}

function ExportCustomersButton() {
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const brandData = useCurrentBrandData();
  const toast = useToast();

  const handleClick = useCallback(async () => {
    setIsLoading(true);

    try {
      const { default: xlsx } = await import("xlsx");

      const { data } = await client.query({
        query,
        variables: brandData?.id ? { brandId: brandData.id } : undefined,
        fetchPolicy: "network-only",
      });

      if (!data?.brandOrderingPlatformsList) {
        throw new Error("Unexpected response returned from query.");
      }

      if (!data.brandOrderingPlatformsList.length) {
        throw new ToastError("No customers were found to export.");
      }

      const workSheetsDataByKey = getSheetData(data.brandOrderingPlatformsList);

      const workBook = await getWorkBookExport(
        WORKSHEET_DEFINITIONS,
        workSheetsDataByKey,
        columnFormats,
      );

      xlsx.writeFile(workBook, `Customers for ${brandData?.name || ""}.xlsx`);
    } catch (e) {
      if (e instanceof ToastError) {
        toast(e.toastOptions);
      } else if (e instanceof ApolloError) {
        toast({
          title: "An error occurred fetching the export data",
          description: e.message,
        });
      } else {
        throw e;
      }
    } finally {
      setIsLoading(false);
    }
  }, [brandData?.name, brandData?.id, client, toast]);

  return (
    <ExportButton
      onClick={asyncVoidHandler(handleClick)}
      isLoading={isLoading}
      variant="outline"
    />
  );
}

export default ExportCustomersButton;
